/**
 * useQcpDataRequests — the 30s poll behind Merlin's data requests ("asks")
 * on the Quality door: the request list plus the counts the header badge and
 * the "Answer received" banner read. Owned by the surface that renders the
 * asks (the workspace and the Ask tab); goes dark when the deploy has the
 * feature flagged off (the list endpoint 404s).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  listDataRequests,
  type DoorDataRequestRead,
} from '../../services/qcpDataRequests';

const POLL_INTERVAL_MS = 30000; // matches the portal notification polling cadence

export interface QcpDataRequestsState {
  requests: DoorDataRequestRead[];
  /** Requests still waiting on the target (drives the header count). */
  openCount: number;
  /** Requests with an answer the user hasn't viewed (drives the red dot). */
  unseenCount: number;
  /**
   * False when the deploy has the feature flagged off (the endpoint 404s) —
   * the page hides the pill/asks/banner entirely so the surface stays dark.
   */
  available: boolean;
  refresh: () => void;
  /** Fold a mutated request (answer/nudge/dismiss/seen response) into the list. */
  applyUpdate: (updated: DoorDataRequestRead) => void;
}

/**
 * Owns the door's data-request list while the page is mounted: initial fetch +
 * 30s polling (paused when `enabled` is false, e.g. before auth resolves).
 */
export function useQcpDataRequests(enabled: boolean): QcpDataRequestsState {
  const [requests, setRequests] = useState<DoorDataRequestRead[]>([]);
  const [available, setAvailable] = useState(true);

  const refresh = useCallback(() => {
    listDataRequests()
      .then((data) => {
        setAvailable(true);
        setRequests(data);
      })
      .catch((error: unknown) => {
        // Feature flagged off on this deploy → endpoint 404s → go dark.
        const status = (error as { response?: { status?: number } })?.response
          ?.status;
        if (status === 404) setAvailable(false);
        // otherwise: polling is best-effort; keep what we have
      });
  }, []);

  useEffect(() => {
    if (!enabled) return;
    refresh();
    const interval = setInterval(refresh, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [enabled, refresh]);

  const applyUpdate = useCallback((updated: DoorDataRequestRead) => {
    // Upsert: a send from a Do next desk can mint rows the list has not
    // polled yet (a team added on the modal), and they belong on the desk
    // at once, not after the next 30s poll.
    setRequests((prev) =>
      prev.some((r) => r.id === updated.id)
        ? prev.map((r) => (r.id === updated.id ? updated : r))
        : [updated, ...prev],
    );
  }, []);

  const openCount = useMemo(
    () => requests.filter((r) => r.status === 'open').length,
    [requests],
  );
  const unseenCount = useMemo(
    () => requests.filter((r) => r.answer_unseen).length,
    [requests],
  );

  return { requests, openCount, unseenCount, available, refresh, applyUpdate };
}
